import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import $670gB$react, { useCallback, useEffect, cloneElement } from "react";
import assert from "assert";
import { Stack } from "../Stack/Stack.mjs";
import { Inline } from "../Inline/Inline.mjs";
import { Columns } from "../Columns/Columns.mjs";
import { Column } from "../Column/Column.mjs";
import { ContentBlock } from "../ContentBlock/ContentBlock.mjs";
import { Box } from "../Box/Box.mjs";
import { Text } from "../Text/Text.mjs";
import { TextLinkButton } from "../TextLinkButton/TextLinkButton.mjs";
import "../Text/TextContext.mjs";
import "../Heading/HeadingContext.mjs";
import { lineHeightContainer } from "../../css/lineHeightContainer.css.mjs";
import { buildDataAttributes } from "../private/buildDataAttributes.mjs";
import "../../css/reset/reset.css.mjs";
import "../../css/atoms/sprinkles.css.mjs";
import "../../hooks/useIcon/icon.css.mjs";
import "../../css/typography.css.mjs";
import { IconCheckCircleOutline } from "../icons/IconCheckCircleOutline/IconCheckCircleOutline.mjs";
import { IconClose } from "../icons/IconClose/IconClose.mjs";
import { IconErrorOutline } from "../icons/IconErrorOutline/IconErrorOutline.mjs";
import { IconInfo } from "../icons/IconInfo/IconInfo.mjs";
import { ButtonIcon } from "../ButtonIcon/ButtonIcon.mjs";
import { useTimeout } from "./useTimeout.mjs";
import { toastVariants } from "./Toast.css.mjs";
const toneToIcon = {
  critical: IconErrorOutline,
  positive: IconCheckCircleOutline,
  info: IconInfo
};
const toastDuration = 6e3;
const borderRadius = "small";
const Action = ({ label, onClick, removeToast }) => {
  const handleClick = useCallback(() => {
    removeToast();
    onClick();
  }, [removeToast, onClick]);
  return /* @__PURE__ */ jsx(Text, { baseline: false, children: /* @__PURE__ */ jsx(Box, { component: "span", paddingRight: "xsmall", "aria-hidden": true, children: /* @__PURE__ */ jsx(TextLinkButton, { onClick: handleClick, hitArea: "large", children: label }) }) });
};
const Toast = $670gB$react.forwardRef(
  ({
    id,
    vanillaTheme,
    dedupeKey,
    message,
    description = null,
    tone,
    icon,
    onClose,
    closeLabel = "Close",
    action,
    shouldRemove,
    data,
    ...restProps
  }, ref) => {
    const remove = useCallback(() => onClose(dedupeKey, id), [onClose, dedupeKey, id]);
    const { stopTimeout, startTimeout } = useTimeout({
      duration: toastDuration,
      onTimeout: remove
    });
    useEffect(() => {
      if (shouldRemove) {
        stopTimeout();
        remove();
      }
    }, [shouldRemove, remove, stopTimeout]);
    const Icon = tone !== "neutral" ? toneToIcon[tone] : void 0;
    assert(
      !icon || icon.props.size === void 0 && icon.props.tone === void 0,
      "Icons cannot set the 'size' or 'tone' prop when passed to a Toast component"
    );
    assert(
      !icon || icon && tone === "neutral",
      `Icons cannot be customised on a Toast component using '${tone}' tone`
    );
    const content = description ? /* @__PURE__ */ jsxs(Stack, { space: "xxsmall", children: [
      /* @__PURE__ */ jsx(Text, { weight: "medium", baseline: false, children: message }),
      typeof description === "string" ? /* @__PURE__ */ jsx(Text, { baseline: false, tone: "secondary", children: description }) : /* @__PURE__ */ jsx(Fragment, { children: description }),
      action ? /* @__PURE__ */ jsx(Action, { removeToast: remove, ...action }, action.label) : null
    ] }) : /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", children: [
      /* @__PURE__ */ jsx(Box, { paddingRight: "medium", children: /* @__PURE__ */ jsx(Text, { weight: "medium", baseline: false, children: message }) }),
      action ? /* @__PURE__ */ jsx(Action, { removeToast: remove, ...action }, action.label) : null
    ] });
    return /* @__PURE__ */ jsx(
      Box,
      {
        display: "flex",
        justifyContent: "flexEnd",
        role: "alert",
        ref,
        onMouseEnter: stopTimeout,
        onMouseLeave: startTimeout,
        className: vanillaTheme,
        ...buildDataAttributes({ data, validateRestProps: restProps }),
        children: /* @__PURE__ */ jsx(Box, { boxShadow: "large", borderRadius, children: /* @__PURE__ */ jsx(ContentBlock, { width: "xsmall", children: /* @__PURE__ */ jsx(
          Box,
          {
            background: "white",
            position: "relative",
            borderRadius,
            paddingY: "small",
            paddingLeft: "small",
            overflow: "hidden",
            className: toastVariants[tone],
            children: /* @__PURE__ */ jsxs(Columns, { space: "none", children: [
              /* @__PURE__ */ jsxs(Column, { width: "content", children: [
                Icon ? /* @__PURE__ */ jsx(Box, { paddingRight: "small", children: /* @__PURE__ */ jsx(Icon, { tone }) }) : null,
                tone === "neutral" && icon ? /* @__PURE__ */ jsx(Box, { paddingRight: "small", children: cloneElement(icon, { tone: "secondary" }) }) : null
              ] }),
              /* @__PURE__ */ jsx(Column, { children: content }),
              /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(
                Box,
                {
                  width: "touchable",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  className: lineHeightContainer.standard,
                  "aria-hidden": true,
                  children: /* @__PURE__ */ jsx(
                    ButtonIcon,
                    {
                      id: `${dedupeKey}-clear`,
                      icon: /* @__PURE__ */ jsx(IconClose, {}),
                      tone: "secondary",
                      variant: "transparent",
                      onClick: remove,
                      label: closeLabel,
                      data: process.env.NODE_ENV !== "production" ? { testid: "clearToast" } : {}
                    }
                  )
                }
              ) })
            ] })
          }
        ) }) })
      }
    );
  }
);
export {
  Toast
};
